<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid, reset, errors}" ref="observer">
    <artisio-modal size="xl" :visible="modal.show" @show="onShow" :title="$t('Auction Results Sync')"
                   :content-in-form="false" @hidden="onHidden" :show-confirmation="showConfirmation"
                   :is-loading="loading" scrollable modal-class="mobile-full-screen"
                   body-class="auction-results-sync-modal p-0">


      <spinner show v-if="loading"/>
      <table class="table sync-table mb-0" v-else-if="liveAuctionProvider">
        <tr class="head-row">
          <td style="width: 25%">{{ $t("Current State") }}</td>
          <td v-for="lp in liveAuctionProvider" :key="`provider-th-table-${lp.provider_key}`">
            {{ lp.provider_name }}
            <font-awesome-icon icon="star" class="yellow-primary-color mb-1" v-if="lp.main" style="font-size: .8em"/>
          </td>
          <td style="width: 4em"></td>
        </tr>
        <tr class="body-rows" v-for="r in items" :key="`row-${r.uuid}-${r.has_conflict}`"
            :class="{'has-conflict': r.has_conflict}">
          <td>
            <auction-results-sync-cell-table :item="r" :currency="currency"/>
          </td>

          <auction-results-sync-cell v-for="lp in liveAuctionProvider" :sale-results="r.lot.sale_results"
                                     :key="`provider-td-table-${lp.provider_key}-${r.uuid}`" :live-provider="lp"/>

          <td class="align-middle">
            <b-tooltip :target="`approve-btn-results-sync-${r.uuid}`" :title="$t('Approve')"/>
            <b-button size="sm" variant="primary" :id="`approve-btn-results-sync-${r.uuid}`"
                      @click="onApproveDisapproveClick(r, true)">
              <font-awesome-icon icon="check"></font-awesome-icon>
            </b-button>
            <br>
            <b-tooltip :target="`disapprove-btn-results-sync-${r.uuid}`" :title="$t('Disapprove')"/>
            <b-button size="sm" variant="danger" class="mt-2" :id="`disapprove-btn-results-sync-${r.uuid}`"
                      @click="onApproveDisapproveClick(r, false)">
              <font-awesome-icon icon="undo"></font-awesome-icon>
            </b-button>
          </td>
        </tr>
      </table>

      <template v-slot:modal-footer>
        <div class="d-flex justify-content-between align-items-center w-100">
          <showing-total-text v-if="pagination && pagination.total" :pagination="pagination"></showing-total-text>
          <per-page class="artisio-table-per-page ml-auto" :pagination="pagination" v-if="pagination"
                    :limit-variants="limitVariants"
                    @limitChange="onLimitChange"/>
          <paginator class="ml-2" :pagination="pagination"
                     @pageChange="onPageChange"/>
        </div>
      </template>


    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import ArtisioModal from "../../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from "vuex";
  import ShowingTotalText from "@/core/components/paginator/ShowingTotalText";
  import PerPage from "@/core/components/paginator/PerPage";
  import Paginator from "@/core/components/paginator/Paginator";
  import LotsService from "@/modules/lots/LotsService";
  import Spinner from "@/core/components/common/Spinner";
  import AuctionResultsSyncCellTable from "@/modules/lots/auction-results-sync-modal/AuctionResultsSyncCellTable";
  import AuctionResultsSyncCell from "@/modules/lots/auction-results-sync-modal/AuctionResultsSyncCell";
  import cloneDeep from "lodash/cloneDeep";

  const {mapState, mapActions} = createNamespacedHelpers('lots');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  let getDefaultPagination = () => {
    return {
      limit: 50,
      page: 1,
      total: 0,
    }
  }

  export default {
    name: "AuctionResultsSyncModal",
    components: {
      AuctionResultsSyncCell,
      AuctionResultsSyncCellTable,
      Spinner,
      Paginator,
      PerPage,
      ShowingTotalText,
      ArtisioModal
    },
    data() {
      return {
        loading: false,
        showConfirmation: true,
        pagination: getDefaultPagination(),
        previousPagination: getDefaultPagination(),
        limitVariants: [5, 10, 25, 50, 200],
        items: [],
        currentExpands: [
          'lot.sale_results.bidder', 'lot.sale_results.customer', 'lot.bidder.customer'
        ]
      }
    },
    computed: {
      ...mapState({
        modal: state => state.auctionResultsSyncModal,
        sale: state => state.auctionResultsSyncModal.sale,
      }),
      ...globalMapState({
        liveAuctionProvider: state => state.liveAuctionProvider
      }),
      currency() {
        return this.sale ? this.sale.currency : null;
      },
      hasAtLeastOneChangedLot() {
        if (!this.items || this.items.length === 0) {
          return false
        }

        for (let r of this.items) {
          let currentSaleResult = r.lot.sale_results.find(i => i.checked)

          if ((!currentSaleResult && r.lot.source_of_sale) || (currentSaleResult && currentSaleResult.third_party_provider_key !== r.lot.source_of_sale)) {
            return true
          }
        }

        return false
      }
    },
    methods: {
      ...mapActions(['hideAuctionResultsSyncModal']),
      onShow() {
        this.showConfirmation = true;
        this.getTableData()
      },
      onHidden() {
        this.hideAuctionResultsSyncModal();
        this.items = []
        this.pagination = getDefaultPagination()
      },
      async getTableData() {
        this.loading = true
        const response = await LotsService.getReservations({
          ...this.pagination,
          auction_uuid: this.sale.uuid,
          has_lot_no: 'true',
          ordering: 'lot_no',
          expand: this.currentExpands.join(',')
        });

        if (response.success) {
          this.items = this.calculateAll(response.body.results)
          this.pagination.total = response.body.count
        }

        this.loading = false
      },
      async onLimitChange(value) {
        if (this.hasAtLeastOneChangedLot) {
          let success = await this.$confirm(this.$i18n.t('You have changes that are not submitted. Are you sure you want to leave current page?'))
          if (!success) {
            this.pagination = cloneDeep(this.previousPagination)
            return
          }
        }

        this.pagination.limit = value;
        this.pagination.page = 1;
        this.getTableData()
        this.previousPagination = cloneDeep(this.pagination);
      },
      async onPageChange(page) {
        if (this.hasAtLeastOneChangedLot) {
          let success = await this.$confirm(this.$i18n.t('You have changes that are not submitted. Are you sure you want to leave current page?'))
          if (!success) {
            this.pagination = cloneDeep(this.previousPagination)
            return
          }
        }

        this.pagination.page = page;
        this.getTableData()
        this.previousPagination = cloneDeep(this.pagination);
      },
      calculateConflicts(item) {
        if (item.lot.is_result_approved || !item.lot.sale_results || item.lot.sale_results.length === 0) {
          item.has_conflict = false;
          return
        }

        let status = item.status
        let hammer_price = item.lot.hammer_price
        let paddle_number = item.lot.paddle_number

        for (let saleResult of item.lot.sale_results) {
          if (saleResult.hammer_price !== hammer_price
              || saleResult.paddle_number !== paddle_number
              || saleResult.status !== status) {
            item.has_conflict = true;
            break
          }
        }
      },
      calculateAll(items) {
        for (let item of items) {
          item.lot.initial_source_of_sale = item.lot.source_of_sale;
          this.calculateConflicts(item)
          for (let saleResult of item.lot.sale_results) {
            saleResult.checked = item.lot.source_of_sale === saleResult.third_party_provider_key
          }
        }
        return items
      },
      async onApproveDisapproveClick(item, isResultApproved) {
        const currentSaleResult = item.lot.sale_results.find(sl => sl.checked === true)


        let msg = this.$i18n.t('Are you sure you want to approve following state?')
        if (!isResultApproved) {
          msg = this.$i18n.t('Are you sure you want to disapprove following state?')
        }
        let success = await this.$confirm(msg)
        if (!success) {
          return
        }

        this.loading = true
        let response = await LotsService.setSourceOfSale({
          lot_uuid: item.lot.uuid,
          source_of_sale: currentSaleResult ? currentSaleResult.third_party_provider_key : null,
          is_result_approved: isResultApproved
        });

        if (response.success) {
          this.$toast('success', this.$i18n.t('Lot state was successfully saved'))

          let tmpResponse = await LotsService.getReservation(item.uuid, this.currentExpands.join(','))
          if (tmpResponse.success) {
            let index = this.items.findIndex(i => i.uuid === item.uuid)
            this.items[index] = {...this.items[index], ...tmpResponse.body};
            this.items = this.calculateAll([...this.items]);
          }

        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0], this.$i18n.t('Auction Results Sync Validation'))
          }
        }

        this.loading = false
      },
    },
  }
</script>

<style lang="scss">
  @import "./../scss/variables";

  .auction-results-sync-modal {
    .sync-table {
      tr.head-row {
        td {
          &:first-child {
            border-right: 2px solid lighten($dark, 15);
          }

          border-top: none;
        }
      }

      tr:nth-child(2) {
        td {
          border-top: 2px solid lighten($dark, 15);
        }
      }

      tr.body-rows {

        font-size: .9em;

        > td {
          &:first-child {
            border-right: 2px solid lighten($dark, 15);
          }
        }

        &.has-conflict {
          background-color: lighten($danger, 25);
        }
      }
    }

  }
</style>
